import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { telematicsAPI, driversAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Gauge,
  Search,
  Loader2,
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Car,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  Zap,
  Moon,
  Trash2,
  FileText,
  Paperclip,
  Download
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DisciplinaryCell = ({ record, type, onUpload, onView }) => {
  const docs = record.disciplinary_documents || [];
  if (docs.length === 0) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs gap-1"
        onClick={() => onUpload(type, record)}
        disabled={!record.driver_id}
        title={record.driver_id ? 'Upload disciplinary document' : 'Assign a driver first'}
        data-testid={`disciplinary-upload-${type}-${record.id}`}
      >
        <Paperclip className="w-3 h-3" />
        Upload
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 px-2 text-xs gap-1 text-emerald-700 hover:bg-emerald-50"
      onClick={() => onView(record)}
      data-testid={`disciplinary-view-${type}-${record.id}`}
    >
      <FileText className="w-3 h-3" />
      {docs.length} doc{docs.length > 1 ? 's' : ''}
    </Button>
  );
};

export const TelematicsPage = () => {
  const [searchParams] = useSearchParams();
  const { user, isSupervisor, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('speeding');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Data
  const [speedingRecords, setSpeedingRecords] = useState([]);
  const [fatigueRecords, setFatigueRecords] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [stats, setStats] = useState(null);
  const [bannerStatus, setBannerStatus] = useState(null);
  
  // Filters
  const [dateFilter, setDateFilter] = useState('');
  const [registrationFilter, setRegistrationFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [hasCaFilter, setHasCaFilter] = useState('');
  const [speedMinFilter, setSpeedMinFilter] = useState('');
  const [hasViolationsFilter, setHasViolationsFilter] = useState('');
  const [uploadBatchFilter, setUploadBatchFilter] = useState('');
  const [uploadDateFilter, setUploadDateFilter] = useState('');
  
  // Selection for bulk assign/delete
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showFiltersExpanded, setShowFiltersExpanded] = useState(false);
  const [bulkAssignDriver, setBulkAssignDriver] = useState('');
  const [bulkAssignRegistration, setBulkAssignRegistration] = useState('');
  const [bulkAssignStartDate, setBulkAssignStartDate] = useState('');
  
  // Export options
  const [exportType, setExportType] = useState('current');
  const [groupByDriver, setGroupByDriver] = useState(true);
  const [bulkAssignEndDate, setBulkAssignEndDate] = useState('');
  
  // Upload refs
  const speedingFileRef = React.useRef(null);
  const fatigueFileRef = React.useRef(null);
  const disciplinaryFileRef = React.useRef(null);
  const [disciplinaryTarget, setDisciplinaryTarget] = useState(null); // { type: 'speeding'|'fatigue', recordId }
  const [disciplinaryPreview, setDisciplinaryPreview] = useState(null); // { record, docs }
  const [uploadingDisciplinary, setUploadingDisciplinary] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build filter params for speeding
      const speedingParams = {
        date: dateFilter || undefined,
        registration: registrationFilter || undefined,
        driver_id: driverFilter || undefined,
        unassigned_only: unassignedOnly,
        has_ca: hasCaFilter === 'yes' ? true : hasCaFilter === 'no' ? false : undefined,
        speed_min: speedMinFilter ? parseFloat(speedMinFilter) : undefined,
        upload_id: uploadBatchFilter || undefined,
        upload_date: uploadDateFilter || undefined
      };
      
      // Build filter params for fatigue
      const fatigueParams = {
        date: dateFilter || undefined,
        registration: registrationFilter || undefined,
        driver_id: driverFilter || undefined,
        unassigned_only: unassignedOnly,
        has_violations: hasViolationsFilter === 'yes' ? true : hasViolationsFilter === 'no' ? false : undefined,
        has_ca: hasCaFilter === 'yes' ? true : hasCaFilter === 'no' ? false : undefined,
        upload_id: uploadBatchFilter || undefined,
        upload_date: uploadDateFilter || undefined
      };
      
      const [speedingRes, fatigueRes, driversRes, regsRes, uploadsRes, statsRes, bannerRes] = await Promise.all([
        telematicsAPI.listSpeeding(speedingParams),
        telematicsAPI.listFatigue(fatigueParams),
        driversAPI.list(),
        telematicsAPI.getRegistrations(),
        telematicsAPI.listUploads(),
        telematicsAPI.getStats({ date: dateFilter || undefined }),
        telematicsAPI.getBannerStatus()
      ]);
      
      setSpeedingRecords(speedingRes.data || []);
      setFatigueRecords(fatigueRes.data || []);
      setDrivers(driversRes.data || []);
      // Filter out empty registration numbers
      setRegistrations((regsRes.data || []).filter(reg => reg && reg.trim() !== ''));
      setUploads(uploadsRes.data || []);
      setStats(statsRes.data);
      setBannerStatus(bannerRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateFilter, registrationFilter, driverFilter, unassignedOnly, hasCaFilter, speedMinFilter, hasViolationsFilter, uploadBatchFilter, uploadDateFilter]);

  // Helper to format uploaded timestamp as date + time
  const formatUploadedAt = (record) => {
    // Try uploaded_at first (full timestamp), then fall back to upload_date + created_at time
    if (record.uploaded_at) {
      const dt = new Date(record.uploaded_at);
      return dt.toLocaleString('en-ZA', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
    // Fallback for older records
    if (record.created_at) {
      const dt = new Date(record.created_at);
      return dt.toLocaleString('en-ZA', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
    return record.upload_date || '-';
  };

  // Clear all filters
  const clearAllFilters = () => {
    setDateFilter('');
    setRegistrationFilter('');
    setDriverFilter('');
    setUnassignedOnly(false);
    setHasCaFilter('');
    setSpeedMinFilter('');
    setHasViolationsFilter('');
    setUploadBatchFilter('');
    setUploadDateFilter('');
  };

  const handleUploadSpeeding = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const res = await telematicsAPI.uploadSpeeding(file, user.id, `${user.first_name} ${user.last_name}`);
      toast.success(res.data.message);
      await fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload speeding report');
    } finally {
      setUploading(false);
      if (speedingFileRef.current) speedingFileRef.current.value = '';
    }
  };

  const handleUploadFatigue = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const res = await telematicsAPI.uploadFatigue(file, user.id, `${user.first_name} ${user.last_name}`);
      toast.success(res.data.message);
      await fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload fatigue report');
    } finally {
      setUploading(false);
      if (fatigueFileRef.current) fatigueFileRef.current.value = '';
    }
  };

  const handleAssignDriver = async (recordId, driverId, type) => {
    try {
      if (type === 'speeding') {
        await telematicsAPI.assignDriverSpeeding([recordId], driverId, user.id, `${user.first_name} ${user.last_name}`);
      } else {
        const res = await telematicsAPI.assignDriverFatigue([recordId], driverId, user.id, `${user.first_name} ${user.last_name}`);
        if (res.data.violations_found > 0) {
          toast.warning(`Fatigue violations detected! Corrective action created.`);
        }
      }
      toast.success('Driver assigned');
      await fetchData();
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to assign driver');
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignDriver) {
      toast.error('Please select a driver');
      return;
    }
    
    if (selectedRecords.length > 0) {
      // Assign to selected records
      try {
        if (activeTab === 'speeding') {
          await telematicsAPI.assignDriverSpeeding(selectedRecords, bulkAssignDriver, user.id, `${user.first_name} ${user.last_name}`);
        } else {
          const res = await telematicsAPI.assignDriverFatigue(selectedRecords, bulkAssignDriver, user.id, `${user.first_name} ${user.last_name}`);
          if (res.data.violations_found > 0) {
            toast.warning(`${res.data.violations_found} fatigue violation(s) detected! Corrective actions created.`);
          }
        }
        toast.success(`Driver assigned to ${selectedRecords.length} records`);
        setSelectedRecords([]);
        setShowBulkAssignDialog(false);
        await fetchData();
      } catch (error) {
        toast.error('Failed to assign driver');
      }
    } else if (bulkAssignRegistration) {
      // Bulk assign by registration
      try {
        if (activeTab === 'speeding') {
          const res = await telematicsAPI.bulkAssignSpeeding(
            bulkAssignRegistration, 
            bulkAssignDriver, 
            bulkAssignStartDate || null, 
            bulkAssignEndDate || null,
            user.id, 
            `${user.first_name} ${user.last_name}`
          );
          toast.success(`Driver assigned to ${res.data.modified_count} speeding records`);
        } else {
          const res = await telematicsAPI.bulkAssignFatigue(
            bulkAssignRegistration, 
            bulkAssignDriver, 
            bulkAssignStartDate || null, 
            bulkAssignEndDate || null,
            user.id, 
            `${user.first_name} ${user.last_name}`
          );
          toast.success(`Driver assigned to ${res.data.modified_count} fatigue records`);
          if (res.data.violations_found > 0) {
            toast.warning(`${res.data.violations_found} fatigue violation(s) detected! Corrective actions created.`);
          }
        }
        setShowBulkAssignDialog(false);
        await fetchData();
      } catch (error) {
        toast.error('Failed to bulk assign driver');
      }
    } else {
      toast.error('Please select records or specify a registration number');
    }
  };

  const handleProcessSpeedingViolations = async () => {
    try {
      const res = await telematicsAPI.processSpeedingViolations(dateFilter || null);
      if (res.data.corrective_actions_created > 0) {
        toast.success(`${res.data.corrective_actions_created} corrective action(s) created for speeding violations`);
      } else {
        toast.info('No new corrective actions needed');
      }
      await fetchData();
    } catch (error) {
      toast.error('Failed to process violations');
    }
  };

  const handleDismissSpeedingBanner = async () => {
    try {
      await telematicsAPI.dismissSpeedingBanner(user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Speeding report dismissed for today');
      await fetchData();
    } catch (error) {
      toast.error('Failed to dismiss');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) {
      toast.error('No records selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedRecords.length} record(s)? This cannot be undone.`)) {
      return;
    }
    
    try {
      setDeleting(true);
      if (activeTab === 'speeding') {
        const res = await telematicsAPI.bulkDeleteSpeeding(selectedRecords, user.id, `${user.first_name} ${user.last_name}`);
        toast.success(res.data.message);
      } else {
        const res = await telematicsAPI.bulkDeleteFatigue(selectedRecords, user.id, `${user.first_name} ${user.last_name}`);
        toast.success(res.data.message);
      }
      setSelectedRecords([]);
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete records');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = () => {
    if (activeTab === 'speeding') {
      telematicsAPI.exportSpeeding(
        exportType === 'current' ? dateFilter : null,
        exportType === 'current' ? registrationFilter : null,
        exportType,
        groupByDriver
      );
    } else {
      telematicsAPI.exportFatigue(
        exportType === 'current' ? dateFilter : null,
        exportType === 'current' ? registrationFilter : null,
        exportType,
        groupByDriver
      );
    }
    setShowExportDialog(false);
  };

  const openDisciplinaryUpload = (type, record) => {
    if (!record.driver_id) {
      toast.error('Assign a driver before uploading a disciplinary document');
      return;
    }
    setDisciplinaryTarget({ type, recordId: record.id });
    // Trigger the hidden file input
    setTimeout(() => disciplinaryFileRef.current?.click(), 0);
  };

  const handleDisciplinaryFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !disciplinaryTarget) return;
    const { type, recordId } = disciplinaryTarget;
    try {
      setUploadingDisciplinary(true);
      const uploader = type === 'speeding'
        ? telematicsAPI.uploadSpeedingDisciplinary
        : telematicsAPI.uploadFatigueDisciplinary;
      await uploader(recordId, file, user.id, `${user.first_name} ${user.last_name}`, '');
      toast.success('Disciplinary document uploaded');
      setDisciplinaryTarget(null);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDisciplinary(false);
    }
  };

  const handleDeleteDisciplinaryDoc = async (type, recordId, docId) => {
    if (!window.confirm('Delete this disciplinary document? This cannot be undone.')) return;
    try {
      const deleter = type === 'speeding'
        ? telematicsAPI.deleteSpeedingDisciplinary
        : telematicsAPI.deleteFatigueDisciplinary;
      await deleter(recordId, docId);
      toast.success('Disciplinary document deleted');
      // Update the preview if open
      if (disciplinaryPreview) {
        const updatedDocs = disciplinaryPreview.docs.filter(d => d.id !== docId);
        if (updatedDocs.length === 0) {
          setDisciplinaryPreview(null);
        } else {
          setDisciplinaryPreview({ ...disciplinaryPreview, docs: updatedDocs });
        }
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleRecordSelection = (recordId) => {
    if (selectedRecords.includes(recordId)) {
      setSelectedRecords(selectedRecords.filter(id => id !== recordId));
    } else {
      setSelectedRecords([...selectedRecords, recordId]);
    }
  };

  const toggleSelectAll = (records) => {
    const recordIds = records.map(r => r.id);
    if (selectedRecords.length === recordIds.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(recordIds);
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return 'Unknown';
    const fn = driver.first_name || driver.user?.first_name || '';
    const ln = driver.last_name || driver.user?.last_name || '';
    return `${fn} ${ln}`.trim() || driver.employee_number || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telematics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            SPEEDING & FATIGUE
          </h1>
          <p className="text-slate-500 mt-1">
            Upload and manage telematics reports
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="file"
            ref={speedingFileRef}
            onChange={handleUploadSpeeding}
            className="hidden"
            accept=".xlsx,.xls"
          />
          <input
            type="file"
            ref={fatigueFileRef}
            onChange={handleUploadFatigue}
            className="hidden"
            accept=".xlsx,.xls"
          />
          
          {(isSupervisor || isAdmin) && (
            <>
              <Button 
                variant="outline"
                onClick={() => speedingFileRef.current?.click()}
                disabled={uploading}
                className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
              >
                <Upload className="w-4 h-4" />
                Upload Speeding
                {bannerStatus?.speeding_uploaded && <CheckCircle2 className="w-4 h-4 text-green-500" />}
              </Button>
              <Button 
                variant="outline"
                onClick={() => fatigueFileRef.current?.click()}
                disabled={uploading}
                className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Upload className="w-4 h-4" />
                Upload Fatigue
                {bannerStatus?.fatigue_uploaded && <CheckCircle2 className="w-4 h-4 text-green-500" />}
              </Button>
              {!bannerStatus?.speeding_uploaded && !bannerStatus?.speeding_dismissed && (
                <Button 
                  variant="ghost"
                  onClick={handleDismissSpeedingBanner}
                  className="text-slate-500"
                  title="No speeding events today"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  No Speeding
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">Speeding</span>
              </div>
              <p className="text-2xl font-bold font-mono">{stats.speeding.total_records}</p>
              <p className="text-xs text-slate-500">{stats.speeding.unassigned} unassigned</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Moon className="w-4 h-4" />
                <span className="text-sm font-medium">Fatigue</span>
              </div>
              <p className="text-2xl font-bold font-mono">{stats.fatigue.total_records}</p>
              <p className="text-xs text-slate-500">{stats.fatigue.unassigned} unassigned</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Violations</span>
              </div>
              <p className="text-2xl font-bold font-mono">{stats.fatigue.with_violations}</p>
              <p className="text-xs text-slate-500">fatigue breaches</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Assigned</span>
              </div>
              <p className="text-2xl font-bold font-mono">{stats.speeding.assigned + stats.fatigue.assigned}</p>
              <p className="text-xs text-slate-500">total records</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Car className="w-4 h-4" />
                <span className="text-sm font-medium">Vehicles</span>
              </div>
              <p className="text-2xl font-bold font-mono">{registrations.length}</p>
              <p className="text-xs text-slate-500">with records</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Today</span>
              </div>
              <p className="text-sm font-medium">
                {bannerStatus?.fatigue_uploaded ? '✓ Fatigue' : '○ Fatigue'}
              </p>
              <p className="text-sm font-medium">
                {bannerStatus?.speeding_uploaded || bannerStatus?.speeding_dismissed ? '✓ Speeding' : '○ Speeding'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          {/* Basic Filters Row */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Event Date:</Label>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Registration:</Label>
              <Select value={registrationFilter || 'all'} onValueChange={(v) => setRegistrationFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {registrations.map(reg => (
                    <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Driver:</Label>
              <Select value={driverFilter || 'all'} onValueChange={(v) => setDriverFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {getDriverName(d.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="unassigned"
                checked={unassignedOnly}
                onCheckedChange={setUnassignedOnly}
              />
              <Label htmlFor="unassigned" className="text-sm cursor-pointer">Unassigned only</Label>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowFiltersExpanded(!showFiltersExpanded)}
              className="gap-1 text-slate-600"
            >
              <Filter className="w-4 h-4" />
              {showFiltersExpanded ? 'Less Filters' : 'More Filters'}
              {showFiltersExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>

          {/* Advanced Filters Row (Expandable) */}
          {showFiltersExpanded && (
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-wrap mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Has CA:</Label>
                <Select value={hasCaFilter || 'all'} onValueChange={(v) => setHasCaFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">With CA</SelectItem>
                    <SelectItem value="no">Without CA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {activeTab === 'speeding' && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Min Speed:</Label>
                  <Select value={speedMinFilter || 'all'} onValueChange={(v) => setSpeedMinFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="90">&gt; 90 km/h</SelectItem>
                      <SelectItem value="100">&gt; 100 km/h</SelectItem>
                      <SelectItem value="110">&gt; 110 km/h</SelectItem>
                      <SelectItem value="120">&gt; 120 km/h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {activeTab === 'fatigue' && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Violations:</Label>
                  <Select value={hasViolationsFilter || 'all'} onValueChange={(v) => setHasViolationsFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">With Violations</SelectItem>
                      <SelectItem value="no">No Violations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Upload Batch:</Label>
                <Select value={uploadBatchFilter || 'all'} onValueChange={(v) => setUploadBatchFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="All Uploads" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Uploads</SelectItem>
                    {uploads
                      .filter(u => activeTab === 'speeding' ? u.report_type === 'speeding' : u.report_type === 'fatigue')
                      .map(u => {
                        // Format the upload time from created_at
                        let uploadTime = '';
                        if (u.created_at) {
                          const dt = new Date(u.created_at);
                          uploadTime = dt.toLocaleString('en-ZA', { 
                            month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', hour12: false
                          });
                        } else {
                          uploadTime = u.upload_date || '';
                        }
                        return (
                          <SelectItem key={u.id} value={u.id}>
                            {uploadTime} - {u.filename?.substring(0, 15) || 'Upload'} ({u.records_count})
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearAllFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Clear All
              </Button>
            </div>
          )}

          {/* Action Buttons Row */}
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center flex-wrap mt-4 pt-4 border-t border-slate-200">
            {selectedRecords.length > 0 && (
              <>
                <Button 
                  onClick={() => setShowBulkAssignDialog(true)}
                  className="gap-2"
                >
                  <User className="w-4 h-4" />
                  Assign Driver ({selectedRecords.length})
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                  className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedRecords.length})
                </Button>
              </>
            )}
            <Button 
              variant="outline"
              onClick={() => {
                setSelectedRecords([]);
                setBulkAssignRegistration('');
                setShowBulkAssignDialog(true);
              }}
              className="gap-2"
            >
              <User className="w-4 h-4" />
              Bulk Assign by Registration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedRecords([]); }}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="speeding" className="gap-2">
              <Zap className="w-4 h-4" />
              Speeding ({speedingRecords.length})
            </TabsTrigger>
            <TabsTrigger value="fatigue" className="gap-2">
              <Moon className="w-4 h-4" />
              Fatigue ({fatigueRecords.length})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            {activeTab === 'speeding' && speedingRecords.some(r => r.driver_id && !r.corrective_action_id) && (
              <Button 
                variant="outline" 
                onClick={handleProcessSpeedingViolations}
                className="gap-2 text-red-600 border-red-300"
              >
                <AlertTriangle className="w-4 h-4" />
                Process Violations
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowExportDialog(true)}
              className="gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Speeding Tab */}
        <TabsContent value="speeding" className="mt-4">
          {speedingRecords.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No speeding records</h3>
              <p className="text-slate-500">Upload a speeding report to see data here</p>
            </div>
          ) : (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedRecords.length === speedingRecords.length && speedingRecords.length > 0}
                          onCheckedChange={() => toggleSelectAll(speedingRecords)}
                        />
                      </TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Uploaded (Date/Time)</TableHead>
                      <TableHead>Registration</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Speed</TableHead>
                      <TableHead>Occurrences</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>CA</TableHead>
                      <TableHead>Disciplinary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {speedingRecords.map((record) => (
                      <TableRow key={record.id} className={!record.driver_id ? 'bg-amber-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRecords.includes(record.id)}
                            onCheckedChange={() => toggleRecordSelection(record.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{record.start_date}</TableCell>
                        <TableCell className="font-mono text-sm">{record.start_time}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap">{formatUploadedAt(record)}</TableCell>
                        <TableCell className="font-medium">{record.registration_number}</TableCell>
                        <TableCell>
                          {record.driver_id ? (
                            <span className="text-green-700">{record.driver_name}</span>
                          ) : (
                            <Select onValueChange={(v) => handleAssignDriver(record.id, v, 'speeding')}>
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <SelectValue placeholder="Assign driver" />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers.map(d => (
                                  <SelectItem key={d.id} value={d.id}>
                                    {getDriverName(d.id)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-800">
                            {record.event_value} km/h
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{record.nr_of_occurrences}</TableCell>
                        <TableCell className="text-sm">{record.total_duration}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                          {record.start_suburb || record.start_city || record.start_street || '-'}
                        </TableCell>
                        <TableCell>
                          {record.corrective_action_id ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : record.driver_id ? (
                            <Badge variant="outline">Pending</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DisciplinaryCell
                            record={record}
                            type="speeding"
                            onUpload={openDisciplinaryUpload}
                            onView={(rec) => setDisciplinaryPreview({ record: rec, type: 'speeding', docs: rec.disciplinary_documents || [] })}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Fatigue Tab */}
        <TabsContent value="fatigue" className="mt-4">
          {fatigueRecords.length === 0 ? (
            <div className="text-center py-12">
              <Moon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No fatigue records</h3>
              <p className="text-slate-500">Upload a fatigue report to see data here</p>
            </div>
          ) : (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedRecords.length === fatigueRecords.length && fatigueRecords.length > 0}
                          onCheckedChange={() => toggleSelectAll(fatigueRecords)}
                        />
                      </TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Uploaded (Date/Time)</TableHead>
                      <TableHead>Registration</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Trip Start</TableHead>
                      <TableHead>Driving Time</TableHead>
                      <TableHead>Standing Time</TableHead>
                      <TableHead>Distance</TableHead>
                      <TableHead>Violations</TableHead>
                      <TableHead>CA</TableHead>
                      <TableHead>Disciplinary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fatigueRecords.map((record) => (
                      <TableRow 
                        key={record.id} 
                        className={record.violations?.length > 0 ? 'bg-red-50' : !record.driver_id ? 'bg-amber-50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedRecords.includes(record.id)}
                            onCheckedChange={() => toggleRecordSelection(record.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{record.date}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap">{formatUploadedAt(record)}</TableCell>
                        <TableCell className="font-medium">{record.registration_number}</TableCell>
                        <TableCell>
                          {record.driver_id ? (
                            <span className="text-green-700">{record.driver_name}</span>
                          ) : (
                            <Select onValueChange={(v) => handleAssignDriver(record.id, v, 'fatigue')}>
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <SelectValue placeholder="Assign driver" />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers.map(d => (
                                  <SelectItem key={d.id} value={d.id}>
                                    {getDriverName(d.id)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{record.trip_start}</TableCell>
                        <TableCell className="font-mono">{record.driving_time}</TableCell>
                        <TableCell className="font-mono">{record.standing_time}</TableCell>
                        <TableCell className="font-mono">{record.distance_km?.toFixed(0) || 0} km</TableCell>
                        <TableCell>
                          {record.violations?.length > 0 ? (
                            <Badge className="bg-red-100 text-red-800">
                              {record.violations.length} violation(s)
                            </Badge>
                          ) : (
                            <span className="text-green-600">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {record.corrective_action_id ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DisciplinaryCell
                            record={record}
                            type="fatigue"
                            onUpload={openDisciplinaryUpload}
                            onView={(rec) => setDisciplinaryPreview({ record: rec, type: 'fatigue', docs: rec.disciplinary_documents || [] })}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Fatigue Rules Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-blue-800">Fatigue Management Rules</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-700 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <strong>Minimum Rest:</strong> 9 hours in 24hr cycle
          </div>
          <div>
            <strong>Maximum Work:</strong> 15 hours per shift
          </div>
          <div>
            <strong>Continuous Driving:</strong> 5 hours or 400km max
          </div>
          <div>
            <strong>Required Break:</strong> 30 minutes after limit
          </div>
        </CardContent>
      </Card>

      {/* Hidden file input for disciplinary uploads */}
      <input
        ref={disciplinaryFileRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleDisciplinaryFileChange}
        data-testid="disciplinary-file-input"
      />

      {/* Disciplinary Documents Preview Dialog */}
      <Dialog open={!!disciplinaryPreview} onOpenChange={(open) => !open && setDisciplinaryPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Disciplinary Documents</DialogTitle>
          </DialogHeader>
          {disciplinaryPreview && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 border-l-2 border-slate-200 pl-3">
                <div><strong>Driver:</strong> {disciplinaryPreview.record.driver_name || 'Unassigned'}</div>
                <div><strong>Vehicle:</strong> {disciplinaryPreview.record.registration_number}</div>
                <div><strong>Event:</strong> {disciplinaryPreview.type === 'speeding'
                  ? `Speeding ${disciplinaryPreview.record.event_value} km/h on ${disciplinaryPreview.record.start_date}`
                  : `Fatigue on ${disciplinaryPreview.record.date}`}
                </div>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {disciplinaryPreview.docs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{doc.filename}</div>
                        <div className="text-xs text-slate-500">
                          Uploaded by {doc.uploaded_by_name || 'Unknown'} · {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={`${API_URL}${doc.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                        title="Open document"
                        data-testid={`disciplinary-open-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteDisciplinaryDoc(disciplinaryPreview.type, disciplinaryPreview.record.id, doc.id)}
                          className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                          title="Delete document"
                          data-testid={`disciplinary-delete-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploadingDisciplinary}
                  onClick={() => {
                    setDisciplinaryTarget({ type: disciplinaryPreview.type, recordId: disciplinaryPreview.record.id });
                    setTimeout(() => disciplinaryFileRef.current?.click(), 0);
                  }}
                  data-testid="disciplinary-add-more"
                >
                  <Paperclip className="w-4 h-4 mr-1" />
                  Upload another
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDisciplinaryPreview(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Assign Driver</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedRecords.length > 0 ? (
              <p className="text-sm text-slate-600">
                Assign driver to <strong>{selectedRecords.length}</strong> selected {activeTab} records
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Registration Number</Label>
                  <Select value={bulkAssignRegistration || 'select'} onValueChange={(v) => setBulkAssignRegistration(v === 'select' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select registration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select">Select registration...</SelectItem>
                      {registrations.map(reg => (
                        <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date (Optional)</Label>
                    <Input
                      type="date"
                      value={bulkAssignStartDate}
                      onChange={(e) => setBulkAssignStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={bulkAssignEndDate}
                      onChange={(e) => setBulkAssignEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label>Driver *</Label>
              <Select value={bulkAssignDriver || 'select'} onValueChange={(v) => setBulkAssignDriver(v === 'select' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">Select driver...</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {getDriverName(d.id)} ({d.employee_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkAssign}>
                Assign Driver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export {activeTab === 'speeding' ? 'Speeding' : 'Fatigue'} Records</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Export Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current View (filtered)</SelectItem>
                  <SelectItem value="all">Full History (all records)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {exportType === 'current' 
                  ? 'Export only records matching your current filters'
                  : 'Export all records regardless of filters'
                }
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="groupByDriver"
                checked={groupByDriver}
                onCheckedChange={setGroupByDriver}
              />
              <Label htmlFor="groupByDriver" className="text-sm cursor-pointer">
                Group by driver (separate sheets)
              </Label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport} className="gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                Export Excel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelematicsPage;
